import { FC, useEffect, useState } from 'react';

import { Dialog } from '@material-ui/core';
import { useForm, useController } from 'react-hook-form';
import {
  BoxV2 as Box,
  Flex,
  Button,
  TextInput,
  MultiSelector,
} from 'portal-commons';

import { isValidInput, toastFlashMessage } from '@/utils';
import { campaignController } from '@/apis/core';
import {
  createMaxLengthValidator,
  createRequiredValidator,
  createXssValidator,
} from '@/utils/validator';

interface RcsCampaignActionModalProps {
  type: 'accept' | 'decline';
  open: boolean;
  campaignUid?: string;
  decliningCampaign?: {
    brandUid: string;
    campaignUid: string;
    downstreamCnpName: string;
  };
  rejectionCategoryOptions?: {
    label: string;
    value: string;
    disabled?: boolean;
  }[];
  onCancel?: () => void;
  onComplete?: (type: RcsCampaignActionModalProps['type']) => void;
  dca2?: boolean;
}

const RcsCampaignActionModal: FC<RcsCampaignActionModalProps> = ({
  campaignUid,
  type,
  open,
  decliningCampaign,
  rejectionCategoryOptions = [],
  onCancel,
  onComplete,
  dca2 = false,
}) => {
  const [processing, setProcessing] = useState(false);
  const { control, handleSubmit, reset, clearErrors } = useForm({});

  useEffect(() => {
    if (open) {
      switch (type) {
        case 'accept':
          reset({
            googleAgentId: '',
          });
          break;
        case 'decline':
          reset({
            rejectionCategory: '',
            explanation: '',
          });
          break;
        default:
          break;
      }
      clearErrors();
      setProcessing(false);
    }
  }, [open]);

  const execAction = async (data) => {
    if (!campaignUid) {
      throw Error('Campaign UID cannot must be provided.');
    }

    setProcessing(true);
    let response: Promise<unknown> | null = null;

    if (type === 'accept') {
      const api = dca2
        ? campaignController.acceptCampaignAsSecondaryDca
        : campaignController.acceptCampaignAsPrimaryDca;
      response = await api(campaignUid, data);
    } else if (type === 'decline') {
      const api = dca2
        ? campaignController.rejectCampaignAsSecondaryDca
        : campaignController.rejectCampaignAsPrimaryDca;
      response = await api(campaignUid, {
        rejectionCategory: data.rejectionCategory.split(','),
        explanation: data.explanation,
      });
    }

    setProcessing(false);
    if (response) {
      if (type === 'accept') {
        toastFlashMessage('RCS Campaign successfully accepted', 'success');
      } else if (type === 'decline') {
        toastFlashMessage('RCS Campaign declined', 'success');
      }

      onComplete?.(type);
    }
  };

  return (
    <Dialog
      data-testid="rcsCampaignActionModal"
      open={open}
      BackdropProps={{
        style: {
          backdropFilter: 'blur(1px)',
        },
      }}
      PaperProps={{
        style: {
          width: '525px',
          maxWidth: 'none',
        },
      }}
    >
      <Box sx={{ px: 'xxl', pt: 'xl', pb: 'xs', lineHeight: 'normal' }}>
        <Box
          as="h3"
          sx={{
            color: 't.black80',
            fontSize: 'H600',
            fontWeight: 'semiBold',
            textAlign: 'center',
            py: 's',
          }}
        >
          {type === 'accept' && 'Accept RCS Campaign'}
          {type === 'decline' && 'Decline RCS Campaign'}
        </Box>
        <form onSubmit={handleSubmit(execAction)} noValidate>
          <Box
            sx={{
              fontSize: 'H300',
              color: 't.grey500',
              lineHeight: 'normal',
            }}
          >
            {type === 'accept' && (
              <AcceptCampaignDialogContent control={control} dca2={dca2} />
            )}

            {type === 'decline' && (
              <DeclineCampaignDialogContent
                control={control}
                campaign={decliningCampaign}
                categoryOptions={rejectionCategoryOptions}
              />
            )}
            <Flex sx={{ gap: 'm', my: '30px', justifyContent: 'center' }}>
              <Button
                onClick={onCancel}
                variant="outline"
                color="primary"
                data-testid="rcsCampaignActionModalCancelButton"
              >
                Cancel
              </Button>
              <Button
                color="primary"
                type="submit"
                data-testid="rcsCampaignActionModalConfirmButton"
                disabled={processing}
              >
                {type === 'accept' && 'Accept'}
                {type === 'decline' && 'Decline'}
              </Button>
            </Flex>
          </Box>
        </form>
      </Box>
    </Dialog>
  );
};

const AcceptCampaignDialogContent = ({ control, dca2 }) => {
  const {
    field: agentIdField,
    fieldState: { error: agentIdError },
  } = useController({
    name: 'googleAgentId',
    control,
    defaultValue: '',
    rules: {
      validate: {
        required: createRequiredValidator('Agent ID is required'),
        maxLength: createMaxLengthValidator(100),
        xss: createXssValidator('Invalid Input'),
      },
    },
  });
  return (
    <Flex sx={{ flexDirection: 'column', gap: 'xs', my: 'xs' }}>
      <Box sx={{ pt: '7px', pb: '11px' }}>
        {dca2 ? (
          <>
            You are accepting the role of Secondary DCA, which requires you to
            provision this campaign with the affiliated MNO.
            <br />
            <br />
            Please provide the Google Agent ID for this campaign, which you can
            find in your Google or MNO RCS dashboard.
          </>
        ) : (
          <>
            You are accepting election as Primary DCA for this campaign. You are
            required to provision it for the MNOs you work with and elect
            Secondary DCAs for any that are remaining.
            <br /> <br />
            Enter the Google Agent ID associated with this campaign. You can
            find it in your Google or MNO RCS dashboard.
          </>
        )}
      </Box>
      <TextInput
        {...agentIdField}
        data-testid="googleAgentId"
        label="Google Agent ID"
        value={agentIdField.value}
        onChange={agentIdField.onChange}
        error={!!agentIdError}
        note={agentIdError?.message}
      />
    </Flex>
  );
};

const DeclineCampaignDialogContent = ({
  control,
  campaign,
  categoryOptions,
}) => {
  const {
    field: rejectionCategory,
    fieldState: { error: rejectionCategoryError },
  } = useController({
    name: 'rejectionCategory',
    control,
    defaultValue: '',
    rules: {
      validate: {
        required: (value: string) => {
          return value?.length > 0 || 'Please select a category';
        },
      },
    },
  });
  const {
    field: explanationField,
    fieldState: { error: explanationError },
  } = useController({
    name: 'explanation',
    control,
    defaultValue: '',
    rules: {
      validate: {
        required: (value: string) =>
          value?.length > 0 || 'Explanation is required',
        maxLength: (value: string) =>
          value?.length <= 2048 || 'Maximum 2048 chars are allowed',
        xss: (val: string) => isValidInput(val) || 'Invalid Input',
      },
    },
  });
  return (
    <Flex sx={{ flexDirection: 'column', gap: 'l', py: 'xs' }}>
      <TextInput label="Campaign ID" readOnly value={campaign?.campaignUid} />
      <TextInput label="Brand ID" readOnly value={campaign?.brandUid} />
      <TextInput
        label="Downstream CNP"
        readOnly
        value={campaign?.downstreamCnpName}
      />
      <MultiSelector
        {...rejectionCategory}
        label="Category *"
        data-testid="rejectionCategory"
        options={categoryOptions}
        value={rejectionCategory.value}
        error={!!rejectionCategoryError?.message}
        helperText={rejectionCategoryError?.message}
      />
      <TextInput
        {...explanationField}
        required
        data-testid="declineExplanation"
        label="Explanation"
        value={explanationField.value}
        onChange={explanationField.onChange}
        error={!!explanationError}
        note={explanationError?.message}
      />
    </Flex>
  );
};

export default RcsCampaignActionModal;
